// biome-ignore-all lint/a11y/noNoninteractiveTabindex: the focusable aggregate image reveals its associated tooltip without implying an action.

"use client";

/**
 * Overlapping initials avatars for the History accordions.
 *
 * Ported from the design exploration "Opportunity History Exploration.html"
 * (`.acc-who`, `.avm` classes): 22px circles, -6px overlap, deterministic
 * accent color per name, hover tooltip listing everyone in the stack.
 */

import { useId } from "react";

const STACK_COLORS = [
  {
    background: "bg-(--accent-cobalt-bg)",
    foreground: "text-(--accent-cobalt-text)",
  },
  {
    background: "bg-(--accent-teal-bg)",
    foreground: "text-(--accent-teal-text)",
  },
  {
    background: "bg-(--accent-plum-bg)",
    foreground: "text-(--accent-plum-text)",
  },
  {
    background: "bg-(--accent-gold-bg)",
    foreground: "text-(--accent-gold-text)",
  },
  {
    background: "bg-(--accent-denim-bg)",
    foreground: "text-(--accent-denim-text)",
  },
  {
    background: "bg-(--accent-rose-bg)",
    foreground: "text-(--accent-rose-text)",
  },
] as const;

export function avatarInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const first = words[0]?.[0] ?? "?";
  const second = words.length > 1 ? (words[words.length - 1]?.[0] ?? "") : "";
  return `${first}${second}`.toUpperCase();
}

function avatarColor(name: string) {
  let hash = 0;
  for (const char of name) hash = (hash + char.charCodeAt(0)) % 997;
  return STACK_COLORS[hash % STACK_COLORS.length] ?? STACK_COLORS[0];
}

export function avatarColorClass(name: string): string {
  return avatarColor(name).background;
}

export function avatarForegroundClass(name: string): string {
  return avatarColor(name).foreground;
}

export interface AvatarStackProps {
  /** Display names, in render order. */
  names: string[];
  /** Max circles before collapsing the rest into a "+N" chip. Default 4. */
  max?: number;
}

export function AvatarStack({ names, max = 4 }: AvatarStackProps) {
  const tooltipId = useId();
  if (names.length === 0) return null;
  const visible = names.slice(0, max);
  const overflow = names.length - visible.length;
  return (
    <span
      className={[
        "group relative inline-flex items-center rounded-full",
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--fg-primary)",
      ].join(" ")}
      role="img"
      aria-label="Authors"
      aria-describedby={tooltipId}
      tabIndex={0}
    >
      {visible.map((name, i) => {
        const color = avatarColor(name);
        return (
          <span
            key={`${name}-${i}`}
            className={[
              "flex h-[22px] w-[22px] items-center justify-center rounded-full",
              "border-2 border-(--bg-white) text-[9.5px] font-bold",
              color.background,
              color.foreground,
              i > 0 ? "-ml-1.5" : "",
            ].join(" ")}
          >
            {avatarInitials(name)}
          </span>
        );
      })}
      {overflow > 0 ? (
        <span className="-ml-1.5 flex h-[22px] min-w-[22px] items-center justify-center rounded-full border-2 border-(--bg-white) bg-(--bg-canvas) px-1 text-[9.5px] font-bold text-(--fg-secondary)">
          +{overflow}
        </span>
      ) : null}
      <span
        id={tooltipId}
        role="tooltip"
        className={[
          "pointer-events-none absolute top-[calc(100%+6px)] right-0 z-10",
          "rounded-md bg-(--brand-navy) px-2.5 py-1.5",
          "text-[11px] whitespace-nowrap text-white",
          "invisible opacity-0",
          "group-hover:visible group-hover:opacity-100",
          "group-focus:visible group-focus:opacity-100",
          "transition-[opacity,visibility]",
        ].join(" ")}
      >
        {names.join(", ")}
      </span>
    </span>
  );
}
